import { InputHTMLAttributes, KeyboardEvent, useState } from 'react';
import { Eye, EyeOff, TriangleAlert } from 'lucide-react';
import { Input } from './Form';
import { NON_ASCII } from '../../lib/password';

export type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Password field with a show/hide toggle and live warnings for Caps Lock and non-English
 * characters. A masked field hides both mistakes, which is how a password gets set to
 * something nobody can type again.
 */
export function PasswordInput({ value, onKeyDown, onKeyUp, ...props }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const text = typeof value === 'string' ? value : '';
  const thai = NON_ASCII.test(text);

  const trackCaps = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState('CapsLock'));

  return (
    <span className="flex flex-col gap-1">
      <span className="relative flex">
        <Input
          {...props}
          value={value}
          type={visible ? 'text' : 'password'}
          className="pr-10"
          spellCheck={false}
          onKeyDown={(e) => {
            trackCaps(e);
            onKeyDown?.(e);
          }}
          onKeyUp={(e) => {
            trackCaps(e);
            onKeyUp?.(e);
          }}
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setVisible((v) => !v);
          }}
          aria-label={visible ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
          title={visible ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-xl text-text-tertiary hover:text-text-primary"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </span>
      {thai && (
        <span className="flex items-center gap-1 text-[11px] font-medium text-bad">
          <TriangleAlert size={12} /> มีตัวอักษรที่ไม่ใช่ภาษาอังกฤษ — คีย์บอร์ดอาจยังเป็นภาษาไทยอยู่
        </span>
      )}
      {capsLock && !thai && (
        <span className="flex items-center gap-1 text-[11px] font-medium text-warn">
          <TriangleAlert size={12} /> Caps Lock เปิดอยู่
        </span>
      )}
    </span>
  );
}
